import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../utils/api';

const AdminDeliverySettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    free_shipping_threshold: 599,
    shipping_fee: 129,
    prepaid_discount: 5,
    cod_enabled: true,
    origin_state: 'Telangana',
  });

  const fetchSettings = async () => {
    try {
      const res = await api.get('/admin/settings');
      setForm((prev) => ({ ...prev, ...res.data }));
    } catch (err) {
      toast.error('Failed to load delivery settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        free_shipping_threshold: Number(form.free_shipping_threshold),
        shipping_fee: Number(form.shipping_fee),
        prepaid_discount: Number(form.prepaid_discount),
        cod_enabled: Boolean(form.cod_enabled),
        origin_state: String(form.origin_state || '').trim(),
      };
      await api.put('/admin/settings', payload);
      toast.success('Delivery settings updated');
      await fetchSettings();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <p className="text-amber-700">Loading delivery settings...</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="text-3xl font-bold text-amber-950 mb-2">Delivery Settings</h1>
      <p className="text-amber-700 mb-6">Manage shipping, prepaid discount and COD availability.</p>

      <form onSubmit={onSave} className="bg-white border border-amber-200 rounded-2xl p-6 space-y-5">
        <div>
          <label className="block text-sm font-medium text-amber-900 mb-1">Free Shipping Threshold (INR)</label>
          <input
            type="number"
            min="0"
            className="w-full rounded-lg border border-amber-200 px-3 py-2"
            value={form.free_shipping_threshold}
            onChange={(e) => setForm({ ...form, free_shipping_threshold: e.target.value })}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-amber-900 mb-1">Shipping Fee (INR)</label>
          <input
            type="number"
            min="0"
            className="w-full rounded-lg border border-amber-200 px-3 py-2"
            value={form.shipping_fee}
            onChange={(e) => setForm({ ...form, shipping_fee: e.target.value })}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-amber-900 mb-1">Prepaid Discount (%)</label>
          <input
            type="number"
            min="0"
            max="100"
            className="w-full rounded-lg border border-amber-200 px-3 py-2"
            value={form.prepaid_discount}
            onChange={(e) => setForm({ ...form, prepaid_discount: e.target.value })}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-amber-900 mb-1">Origin State</label>
          <input
            type="text"
            className="w-full rounded-lg border border-amber-200 px-3 py-2"
            value={form.origin_state}
            onChange={(e) => setForm({ ...form, origin_state: e.target.value })}
          />
        </div>

        <label className="inline-flex items-center gap-2 text-amber-900">
          <input
            type="checkbox"
            checked={form.cod_enabled}
            onChange={(e) => setForm({ ...form, cod_enabled: e.target.checked })}
          />
          Enable Cash on Delivery (COD)
        </label>

        <div className="pt-2">
          <button
            type="submit"
            disabled={saving}
            className="bg-amber-700 hover:bg-amber-800 text-white rounded-lg px-5 py-2.5 font-medium disabled:opacity-60"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminDeliverySettings;
